import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  ArrowLeft, 
  Map, 
  Home, 
  Users, 
  Calendar, 
  BookOpen, 
  Heart, 
  Gift, 
  Camera, 
  Settings,
  Shield,
  HelpCircle,
  Mail,
  Phone,
  MapPin
} from 'lucide-react';

const SiteMap = () => {
  const siteStructure = [
    {
      title: "Pages principales",
      icon: Home,
      color: "blue",
      pages: [
        { name: "Accueil", path: "/", description: "Page d'accueil avec actualités et vie de la communauté" },
        { name: "À propos", path: "/about", description: "Histoire et mission de la communauté FSF" },
        { name: "Contact", path: "/contact", description: "Formulaire de contact et informations pratiques" }
      ]
    },
    {
      title: "Vie communautaire",
      icon: Users,
      color: "green",
      pages: [
        { name: "Membres", path: "/members", description: "Espace des membres de la communauté" },
        { name: "Espace Couples", path: "/couples", description: "Section dédiée aux couples FSF" },
        { name: "Contributions", path: "/contributions", description: "Système de contributions de la communauté" },
        { name: "Zones géographiques", path: "/zones", description: "Organisation par zones géographiques" }
      ]
    },
    {
      title: "Spiritualité",
      icon: BookOpen,
      color: "purple",
      pages: [
        { name: "Lectures du jour", path: "/lectures", description: "Lectures catholiques quotidiennes" },
        { name: "Prières", path: "/prayers", description: "Recueil de prières et méditations" },
        { name: "Ressources spirituelles", path: "/resources", description: "Documents et guides spirituels" },
        { name: "Témoignages", path: "/testimonies", description: "Témoignages de foi de la communauté" }
      ]
    },
    {
      title: "Événements",
      icon: Calendar,
      color: "orange",
      pages: [
        { name: "Calendrier des événements", path: "/events", description: "Tous les événements de la communauté" },
        { name: "Inscriptions", path: "/registrations", description: "S'inscrire aux événements FSF" },
        { name: "Retraites spirituelles", path: "/retreats", description: "Retraites et temps forts spirituels" },
        { name: "Célébrations", path: "/celebrations", description: "Messes et célébrations liturgiques" }
      ]
    },
    {
      title: "Multimédia",
      icon: Camera,
      color: "pink",
      pages: [
        { name: "Galerie photos", path: "/gallery", description: "Photos des événements et activités FSF" },
        { name: "Vidéos", path: "/videos", description: "Vidéos de témoignages et enseignements" },
        { name: "Lumen Christi TV", path: "/lumen-christi", description: "Chaîne YouTube de contenus catholiques" },
        { name: "Podcast FSF", path: "/podcast", description: "Émissions audio spirituelles" }
      ]
    },
    {
      title: "Services",
      icon: Heart,
      color: "red",
      pages: [
        { name: "Accompagnement spirituel", path: "/spiritual-guidance", description: "Direction spirituelle et accompagnement" },
        { name: "Préparation au mariage", path: "/marriage-preparation", description: "Formation pour futurs époux" },
        { name: "Formation continue", path: "/formation", description: "Sessions de formation spirituelle" },
        { name: "Aide sociale", path: "/social-help", description: "Entraide et solidarité communautaire" }
      ]
    },
    {
      title: "Donations et soutien",
      icon: Gift,
      color: "yellow",
      pages: [
        { name: "Faire un don", path: "/donate", description: "Soutenir financièrement la communauté FSF" },
        { name: "Bénévolat", path: "/volunteer", description: "Devenir bénévole dans nos activités" },
        { name: "Partenariats", path: "/partnerships", description: "Partenaires et collaborations" },
        { name: "Projets en cours", path: "/projects", description: "Projets de développement de la communauté" }
      ]
    },
    {
      title: "Administration",
      icon: Settings,
      color: "gray",
      pages: [
        { name: "Tableau de bord", path: "/dashboard", description: "Interface d'administration (membres)" },
        { name: "Profil utilisateur", path: "/profile", description: "Gestion de votre compte personnel" },
        { name: "Paramètres", path: "/settings", description: "Configuration de votre compte" },
        { name: "Historique", path: "/history", description: "Historique de vos activités" }
      ]
    }
  ];

  const legalPages = [
    { name: "Mentions légales", path: "/legal-mentions", description: "Informations légales du site" },
    { name: "Politique de confidentialité", path: "/privacy-policy", description: "Protection de vos données personnelles" },
    { name: "Plan du site", path: "/sitemap", description: "Cette page - navigation complète du site" }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: "from-blue-500 to-blue-600 text-blue-600 bg-blue-50 border-blue-200",
      green: "from-green-500 to-green-600 text-green-600 bg-green-50 border-green-200",
      amber: "from-amber-500 to-amber-600 text-amber-500 bg-amber-50 border-amber-200",
      orange: "from-orange-500 to-orange-600 text-orange-600 bg-orange-50 border-orange-200",
      pink: "from-pink-500 to-pink-600 text-pink-600 bg-pink-50 border-pink-200",
      red: "from-red-500 to-red-600 text-red-600 bg-red-50 border-red-200",
      yellow: "from-yellow-500 to-yellow-600 text-yellow-600 bg-yellow-50 border-yellow-200",
      gray: "from-gray-500 to-gray-600 text-gray-600 bg-gray-50 border-gray-200"
    };
    return colors[color] || colors.blue;
  };

  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const closeContactModal = () => setIsContactModalOpen(false);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header de navigation */}
      <Header />
      
      {/* Header de la page */}
      <div className="bg-gradient-to-r from-fsf-primary to-fsf-secondary text-white py-16">
        <div className="container mx-auto px-4">
          <Link 
            to="/" 
            className="inline-flex items-center space-x-2 text-white/80 hover:text-white transition-colors duration-200 mb-6"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Retour à l'accueil</span>
          </Link>
          
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
              <Map className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold font-serif">Plan du site</h1>
              <p className="text-xl text-white/80 mt-2">Navigation complète de notre site web</p>
            </div>
          </div>
        </div>
      </div>

      {/* Contenu */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-6xl mx-auto">
          
          {/* Introduction */}
          <section className="bg-white rounded-2xl shadow-lg p-8 mb-8">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">
                Découvrez toutes les sections de notre site
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto leading-relaxed">
                Cette page vous présente l'organisation complète du site web de la communauté 
                <strong> Fleurs de la Sainte Famille (FSF)</strong>. Naviguez facilement vers la section qui vous intéresse.
              </p>
            </div>
          </section>

          {/* Statistiques rapides */}
          <section className="grid md:grid-cols-4 gap-4 mb-12">
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 text-center">
              <Home className="w-8 h-8 text-blue-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-blue-800">8</div>
              <div className="text-sm text-blue-600">Sections principales</div>
            </div>
            
            <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
              <BookOpen className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-green-800">30+</div>
              <div className="text-sm text-green-600">Pages de contenu</div>
            </div>
            
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
              <Users className="w-8 h-8 text-amber-500 mx-auto mb-2" />
              <div className="text-2xl font-bold text-amber-700">1000+</div>
              <div className="text-sm text-amber-600">Membres actifs</div>
            </div>
            
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-6 text-center">
              <Heart className="w-8 h-8 text-orange-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-orange-800">24/7</div>
              <div className="text-sm text-orange-600">Contenu spirituel</div>
            </div>
          </section>

          {/* Sections principales */}
          <div className="space-y-8">
            {siteStructure.map((section, index) => {
              const IconComponent = section.icon;
              const colorClasses = getColorClasses(section.color).split(' ');
              
              return (
                <section key={index} className="bg-white rounded-2xl shadow-lg overflow-hidden">
                  <div className={`bg-gradient-to-r ${colorClasses[0]} ${colorClasses[1]} text-white p-6`}>
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                        <IconComponent className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold">{section.title}</h2>
                        <p className="text-white/80">
                          {section.pages.length} page{section.pages.length > 1 ? 's' : ''} disponible{section.pages.length > 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-4">
                      {section.pages.map((page, pageIndex) => (
                        <Link
                          key={pageIndex}
                          to={page.path}
                          className={`block p-4 rounded-lg border transition-all duration-200 hover:shadow-md ${colorClasses[2]} ${colorClasses[3]} hover:scale-105`}
                        >
                          <h3 className={`font-semibold ${colorClasses[1].replace('to-', 'text-')} mb-2`}>
                            {page.name}
                          </h3>
                          <p className="text-gray-600 text-sm leading-relaxed">
                            {page.description}
                          </p>
                        </Link>
                      ))}
                    </div>
                  </div>
                </section>
              );
            })}
          </div>

          {/* Pages légales */}
          <section className="bg-white rounded-2xl shadow-lg mt-12 overflow-hidden">
            <div className="bg-gradient-to-r from-gray-600 to-gray-700 text-white p-6">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">Pages légales et informationnelles</h2>
                  <p className="text-white/80">Informations légales et réglementaires</p>
                </div>
              </div>
            </div>
            
            <div className="p-6">
              <div className="grid md:grid-cols-3 gap-4">
                {legalPages.map((page, index) => (
                  <Link
                    key={index}
                    to={page.path}
                    className="block p-4 rounded-lg border bg-gray-50 border-gray-200 hover:bg-gray-100 transition-all duration-200 hover:shadow-md"
                  >
                    <h3 className="font-semibold text-gray-800 mb-2">{page.name}</h3>
                    <p className="text-gray-600 text-sm">{page.description}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Aide et contact */}
          <section className="bg-gradient-to-r from-fsf-primary to-fsf-secondary text-white rounded-2xl p-8 mt-12">
            <div className="text-center">
              <HelpCircle className="w-16 h-16 mx-auto mb-4 text-white/80" />
              <h2 className="text-2xl font-bold mb-4">Besoin d'aide pour naviguer ?</h2>
              <p className="text-white/80 mb-6 max-w-2xl mx-auto">
                Notre équipe est là pour vous accompagner dans votre découverte du site 
                et de notre communauté. N'hésitez pas à nous contacter !
              </p>
              
              <div className="grid md:grid-cols-3 gap-6 max-w-3xl mx-auto">
                <div className="text-center">
                  <Mail className="w-8 h-8 mx-auto mb-2 text-white/80" />
                  <h3 className="font-semibold mb-1">Email</h3>
                  <p className="text-sm text-white/80">support@fleursdelasaintefamille.org</p>
                </div>
                
                <div className="text-center">
                  <Phone className="w-8 h-8 mx-auto mb-2 text-white/80" />
                  <h3 className="font-semibold mb-1">Téléphone</h3>
                  <p className="text-sm text-white/80">+229 XX XX XX XX</p>
                </div>
                
                <div className="text-center">
                  <MapPin className="w-8 h-8 mx-auto mb-2 text-white/80" />
                  <h3 className="font-semibold mb-1">Localisation</h3>
                  <p className="text-sm text-white/80">Cotonou, Bénin</p>
                </div>
              </div>
              
              <div className="mt-6">
                <Link
                  to="/contact"
                  className="inline-block bg-white text-fsf-primary px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200"
                >
                  Nous contacter
                </Link>
              </div>
            </div>
          </section>

          {/* Footer du plan du site */}
          <div className="text-center mt-12 py-8 border-t border-gray-200">
            <p className="text-gray-500 text-sm">
              Plan du site dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}
            </p>
            <p className="text-gray-400 text-xs mt-2">
              © {new Date().getFullYear()} Communauté Fleurs de la Sainte Famille (FSF) - Tous droits réservés
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default SiteMap;